/**
 * Own the "which baseline is on screen" state for one baseline slot on a Performance page.
 *
 * Clicking a Baseline entry in the legend opens that slot's picker; choosing there loads the
 * baseline's fitted curves and remembers the choice. The page then feeds each slot's fit/config
 * into the charts, which reduce them to a curve per series.
 *
 * A page instantiates this once per slot. The slot is what keys persistence, so slot 1 and slot 2
 * remember independently — including which CONFIG each is showing, which is what makes "same
 * baseline, two configurations" a usable comparison.
 */

import { createSignal, type Accessor } from 'solid-js';
import { user } from '../store/userStore';
import { debug as logDebug, error as logError } from '../utils/console';
import {
  listBaselines,
  fetchBaseline,
  deleteBaseline,
  baselineConfigs,
  isManeuverBaselineFit,
  type BaselineRow,
  type BaselineFitJson,
} from '../services/baselinesService';
import {
  readPerformanceBaselineSession,
  writePerformanceBaselineSession,
  loadPerformanceBaselineFromPersistentSettings,
  savePerformanceBaselineToPersistentSettings,
  type BaselineSlot,
} from '../utils/performanceBaselinePersistence';
import { NO_BASELINE_OPTION } from '../components/modals/BaselineDisplayModal';

/** Matches the target curve's default weight in the legend; overridable in the picker. */
export const DEFAULT_BASELINE_COLOR = '#ffffff';

/**
 * Per-slot default stroke, so two baselines are distinguishable the moment they are both shown
 * without anyone having to pick a colour. Cyan is chosen to clash with neither the white slot-1
 * curve nor the green target.
 */
export const DEFAULT_BASELINE_COLORS: Record<BaselineSlot, string> = {
  1: DEFAULT_BASELINE_COLOR,
  2: '#00FFFF',
};

export interface UseBaselineDisplayOptions {
  getClassName: Accessor<string>;
  getProjectId: Accessor<number | string>;
  /** Which of the two baseline overlays this instance owns. Defaults to slot 1. */
  slot?: BaselineSlot;
  /**
   * What the legend shows for an empty slot. Defaults to `Baseline ${slot}`, which is what
   * distinguishes the two entries on a page carrying both. A page with a single slot passes
   * "Baseline", where a number would only imply a second entry that is not there.
   */
  emptyLabel?: string;
  /** Called after the displayed baseline changes so the page can force a chart redraw. */
  onApplied?: () => void;
  /**
   * Called after this slot deletes a baseline. Deletion is project-wide, so the page uses this to
   * clear the other slot too — otherwise a slot showing the deleted baseline keeps drawing a curve
   * that no longer exists until the page is reloaded.
   */
  onDeleted?: (baselineId: number) => void;
}

export function useBaselineDisplay(options: UseBaselineDisplayOptions) {
  const slot: BaselineSlot = options.slot ?? 1;
  const defaultColor = DEFAULT_BASELINE_COLORS[slot];

  const [baselineId, setBaselineId] = createSignal<number | null>(null);
  const [baselineName, setBaselineName] = createSignal('');
  const [baselineFit, setBaselineFit] = createSignal<BaselineFitJson | null>(null);
  const [baselineConfig, setBaselineConfig] = createSignal('');
  const [baselineColor, setBaselineColor] = createSignal(defaultColor);

  const [showModal, setShowModal] = createSignal(false);
  const [availableBaselines, setAvailableBaselines] = createSignal<BaselineRow[]>([]);
  const [selectedKey, setSelectedKey] = createSignal(NO_BASELINE_OPTION);
  const [modalConfigs, setModalConfigs] = createSignal<string[]>([]);
  const [modalConfig, setModalConfig] = createSignal('');
  /**
   * Curves for whatever the picker currently has selected, applied or not.
   *
   * `loadConfigsForKey` already fetches the row to list its configs; keeping the fit means the
   * Excel export can write the selection the user is looking at rather than the one still drawn
   * behind the modal.
   */
  const [modalFit, setModalFit] = createSignal<BaselineFitJson | null>(null);
  const [modalColor, setModalColor] = createSignal(defaultColor);
  const [loading, setLoading] = createSignal(false);
  const [loadingAction, setLoadingAction] = createSignal<'select' | 'remove' | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  /**
   * Bumped whenever the displayed baseline changes. Charts include it in their redraw signature —
   * without it a new baseline with the same name and colour would not repaint.
   */
  const [overlayRevision, setOverlayRevision] = createSignal(0);

  const contextIds = () => {
    const className = options.getClassName();
    const projectId = Number(options.getProjectId());
    // user_id is a uuid string, which is what persistentSettingsService keys on.
    const rawUserId = (user() as { user_id?: unknown })?.user_id;
    const userId = typeof rawUserId === 'string' && rawUserId !== '' ? rawUserId : null;
    return { className, projectId, userId };
  };

  const persist = (selection: {
    baselineId: number | null;
    baselineName: string;
    baselineConfig: string;
    baselineColor?: string;
  }) => {
    writePerformanceBaselineSession(selection, slot);
    const { className, projectId, userId } = contextIds();
    if (userId == null || !className || !projectId) { return; }
    void savePerformanceBaselineToPersistentSettings(userId, className, projectId, selection, slot)
      .catch((err) => logDebug('[useBaselineDisplay] persist failed', err));
  };

  const clearBaseline = (persistChoice = true) => {
    setBaselineId(null);
    setBaselineName('');
    setBaselineFit(null);
    setBaselineConfig('');
    setOverlayRevision((r) => r + 1);
    if (persistChoice) {
      // Recorded explicitly, so "off" survives a reload instead of auto-selecting again.
      persist({ baselineId: null, baselineName: '', baselineConfig: '' });
    }
    options.onApplied?.();
  };

  /**
   * Load a baseline's curves and show them.
   *
   * `preferredConfig` is honoured when the baseline actually has a fit for it; otherwise the first
   * config is used, so a restored selection whose config has since disappeared still draws.
   */
  const applyBaseline = async (id: number, preferredConfig?: string): Promise<boolean> => {
    const { className, projectId } = contextIds();
    if (!className || !projectId) { return false; }

    const row = await fetchBaseline(className, projectId, id);
    if (!row) {
      logDebug('[useBaselineDisplay] baseline no longer exists', id);
      clearBaseline(false);
      return false;
    }

    // Straight-line slots draw straight-line curves only. The picker lists nothing else, so a
    // maneuver fit here means a stale saved id from the maneuver pages -- show no curve rather than
    // feed a differently-shaped fit to the scatter.
    const fit = isManeuverBaselineFit(row.fit_json) ? null : (row.fit_json || null);
    const configs = baselineConfigs(fit);
    const config = preferredConfig && configs.includes(preferredConfig)
      ? preferredConfig
      : (configs[0] || '');

    setBaselineId(row.baseline_id);
    setBaselineName(row.name);
    setBaselineFit(fit);
    setBaselineConfig(config);
    setOverlayRevision((r) => r + 1);
    persist({
      baselineId: row.baseline_id,
      baselineName: row.name,
      baselineConfig: config,
      baselineColor: baselineColor(),
    });
    options.onApplied?.();
    return true;
  };

  /**
   * Restore the previous choice on mount: session first, then persistent settings.
   *
   * Nothing is auto-selected when the user has no saved preference — a baseline is a deliberate
   * comparison, and silently picking one would make the charts look different for no visible
   * reason.
   */
  const restoreSavedBaseline = async (): Promise<void> => {
    const { className, projectId, userId } = contextIds();
    if (!className || !projectId) { return; }

    let saved = readPerformanceBaselineSession(slot);
    if (!saved && userId != null) {
      saved = await loadPerformanceBaselineFromPersistentSettings(userId, className, projectId, slot);
    }
    if (!saved) { return; }

    if (saved.baselineColor) { setBaselineColor(saved.baselineColor); }
    if (saved.baselineId == null) { return; }   // explicit "No baseline"

    try {
      await applyBaseline(saved.baselineId, saved.baselineConfig);
    } catch (err: unknown) {
      logError('[useBaselineDisplay] restore failed', err);
    }
  };

  const refreshBaselineList = async (): Promise<BaselineRow[]> => {
    const { className, projectId } = contextIds();
    if (!className || !projectId) { return []; }
    // Baselines that have a straight-line fit. A maneuvers-only baseline stays in the maneuver picker.
    const rows = await listBaselines(className, projectId, { kind: 'straight_line' });
    setAvailableBaselines(rows);
    return rows;
  };

  /** Configs offered for a given picker selection. The fit is kept for the Excel export. */
  const loadConfigsForKey = async (key: string) => {
    if (key === NO_BASELINE_OPTION) {
      setModalConfigs([]);
      setModalConfig('');
      setModalFit(null);
      return;
    }
    const id = Number(key);
    if (!Number.isInteger(id) || id <= 0) { return; }

    // The currently displayed baseline's fit is already in hand.
    if (id === baselineId() && baselineFit()) {
      const configs = baselineConfigs(baselineFit());
      setModalConfigs(configs);
      setModalConfig(baselineConfig() || configs[0] || '');
      setModalFit(baselineFit());
      return;
    }

    const { className, projectId } = contextIds();
    try {
      const row = await fetchBaseline(className, projectId, id);
      // Same guard as applyBaseline: a maneuver fit has a different shape and belongs to the other
      // picker, so it is neither drawn nor exported here.
      const fit = isManeuverBaselineFit(row?.fit_json) ? null : (row?.fit_json || null);
      const configs = baselineConfigs(fit);
      setModalConfigs(configs);
      setModalConfig(configs[0] || '');
      setModalFit(fit);
    } catch (err: unknown) {
      logError('[useBaselineDisplay] failed to load configs', err);
      setModalConfigs([]);
      setModalConfig('');
      setModalFit(null);
    }
  };

  const openModal = async () => {
    setError(null);
    setShowModal(true);
    setModalColor(baselineColor());
    const key = baselineId() != null ? String(baselineId()) : NO_BASELINE_OPTION;
    setSelectedKey(key);
    try {
      await refreshBaselineList();
      await loadConfigsForKey(key);
    } catch (err: unknown) {
      logError('[useBaselineDisplay] failed to list baselines', err);
      setError('Could not load baselines.');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setError(null);
  };

  const handleSelectedKeyChange = (value: string) => {
    setSelectedKey(value);
    setError(null);
    void loadConfigsForKey(value);
  };

  const handleConfirm = async () => {
    setError(null);
    const key = selectedKey();
    setBaselineColor(modalColor());

    if (key === NO_BASELINE_OPTION) {
      clearBaseline();
      closeModal();
      return;
    }

    const id = Number(key);
    if (!Number.isInteger(id) || id <= 0) {
      setError('Select a baseline.');
      return;
    }

    setLoading(true);
    setLoadingAction('select');
    try {
      const ok = await applyBaseline(id, modalConfig());
      if (!ok) {
        setError('That baseline could not be loaded.');
        return;
      }
      closeModal();
    } catch (err: unknown) {
      logError('[useBaselineDisplay] select failed', err);
      setError(err instanceof Error ? err.message : 'Could not load that baseline.');
    } finally {
      setLoading(false);
      setLoadingAction(null);
    }
  };

  const handleRemove = async () => {
    const id = Number(selectedKey());
    if (!Number.isInteger(id) || id <= 0) { return; }

    const name = availableBaselines().find((b) => b.baseline_id === id)?.name || 'this baseline';
    if (typeof window !== 'undefined'
      && !window.confirm(`Delete "${name}"? This removes the baseline for everyone on the project.`)) {
      return;
    }

    setLoading(true);
    setLoadingAction('remove');
    try {
      const { className, projectId } = contextIds();
      const result = await deleteBaseline(className, projectId, id);
      if (!result.success) {
        setError(result.message || 'Could not delete that baseline.');
        return;
      }
      if (baselineId() === id) { clearBaseline(); }
      // The baseline is gone for the whole project, so any other slot showing it must drop it too.
      options.onDeleted?.(id);
      await refreshBaselineList();
      setSelectedKey(NO_BASELINE_OPTION);
      setModalConfigs([]);
      setModalConfig('');
      setModalFit(null);
    } catch (err: unknown) {
      logError('[useBaselineDisplay] delete failed', err);
      setError(err instanceof Error ? err.message : 'Could not delete that baseline.');
    } finally {
      setLoading(false);
      setLoadingAction(null);
    }
  };

  /** Drop this slot's baseline if it is the one given. Used when another slot deletes it. */
  const clearIfShowing = (id: number) => {
    if (baselineId() === id) { clearBaseline(); }
  };

  /**
   * Show a baseline in this slot after a save, so new members appear immediately.
   *
   * Unconditional: which slot a just-saved baseline belongs in is a decision across slots, so the
   * page makes it (see `showSavedBaselineInSlots`) and this only carries it out.
   */
  const refreshAfterSave = async (id: number, name: string) => {
    logDebug('[useBaselineDisplay] refreshing after save', { slot, id, name });
    await applyBaseline(id, baselineConfig());
  };

  /**
   * What the legend shows: the baseline and which of its configs is drawn.
   *
   * An empty slot is labelled by number rather than "No baseline", so with two entries side by side
   * it is clear they are two slots and which one you are about to change. A single-slot page
   * overrides that with `emptyLabel`.
   */
  const baselineLabel = () => {
    const name = baselineName();
    if (!name) { return options.emptyLabel || `Baseline ${slot}`; }
    const config = baselineConfig();
    return config ? `${name} — ${config}` : name;
  };

  return {
    slot,
    baselineId,
    baselineName,
    baselineFit,
    baselineConfig,
    baselineColor,
    setBaselineColor,
    baselineLabel,
    overlayRevision,

    showModal,
    availableBaselines,
    selectedKey,
    setSelectedKey: handleSelectedKeyChange,
    modalConfigs,
    modalConfig,
    modalFit,
    setModalConfig,
    modalColor,
    setModalColor,
    loading,
    loadingAction,
    error,

    openModal,
    closeModal,
    handleConfirm,
    handleRemove,
    restoreSavedBaseline,
    refreshAfterSave,
    clearIfShowing,
  };
}
